
import EazyHeading from '@/components/EazyHeading.vue';
import store from '@/store';
import requestAPI from '@/logic/requestBeApi';
import router from '@/router/index';
import currentAssessmentService from '@/logic/services/currentAssessmentService';
import BlueCheckbox from '@/components/BlueCheckbox.vue';
import CountryInput from '@/components/Inputs/CountryInput.vue';
import formValidators from '@/logic/helpers/forms/validators';
import inputValidators from '@/logic/helpers/inputs/validators';

export default {
  name: 'AssessmentPlace',

  components: {
    EazyHeading,
    BlueCheckbox,
    CountryInput,
  },

  props: {
    inModal: Boolean,
    assessmentStage: {
      type: Number,
      default: 0,
    },
    candidateFromModal: {
      type: String,
      default: '',
    },
    companyFromModal: {
      type: String,
      default: '',
    },
    profilePicFromModal: {
      type: String,
      default: '',
    },
    positionFromModal: {
      type: String,
      default: '',
    },
    assessmentIdFromModal: {
      type: String,
      default: '',
    },
    dateTimeFromModal: {
      type: [String, Array],
      default: '',
    },
    assessmentObject: {
      type: Object,
      default: (): Record<string, never> => ({}),
    },
  },

  data(): Record<string, unknown> {
    return {
      router,
      assessmentPlace: {
        type: '',
        street: '',
        house_number: '',
        apartment_number: '',
        city: '',
        postal_code: '',
        country: '',
        callInfo: '',
      },
      validationRules: {
        type: [{ validator: inputValidators.primitive, trigger: 'change' }],
        street: [{ validator: inputValidators.primitive, trigger: 'change' }],
        house_number: [{ validator: inputValidators.primitive, trigger: 'change' }],
        city: [{ validator: inputValidators.primitive, trigger: 'change' }],
        postal_code: [{ validator: inputValidators.primitive, trigger: 'change' }],
        country: [{ validator: inputValidators.primitive, trigger: 'change' }],
      },
      sentInvite: false,
      device: store.state.device,
      processingInvitation: false,
      typesList: [
        { text: this.$t('anOnlineVideoCall'), key: 'an online video-call' },
        { text: this.$t('aPhysicalPlace'), key: 'a physical place' },
      ],
      contactDataCheckboxes: [
        {
          name: 'PersonalPhoneNumber',
          value: store.state.userPersonalPhoneNumber,
          showToCandidate: false,
        },
        {
          name: 'CompanyPhoneNumber',
          value: store.state.userCompanyPhoneNumber,
          showToCandidate: false,
        },
        { name: 'Skype', value: store.state.userSkype, showToCandidate: false },
        { name: 'Zoom', value: store.state.userZoom, showToCandidate: false },
        { name: 'Whereby', value: store.state.userWhereby, showToCandidate: false },
      ],
      currentAssessment: currentAssessmentService.obtainFromLocalStorage(),
      citySetByClient: false,
      countrySetByClient: false,
      streetSetByClient: false,
      postal_codeSetByClient: false,
      house_numberSetByClient: false,
      apartment_numberSetByClient: false,
      alertAboutLocationDataSetByClient: '',
      formIsValid: false,
      parsingPreferredPlace: false,
    };
  },

  computed: {
    candidate(): string {
      return this.$route?.query.cand || this.candidateFromModal;
    },

    company(): string {
      return this.$route?.query.comp || this.companyFromModal;
    },

    profilePic(): string {
      return this.$route?.query.pic || this.profilePicFromModal;
    },

    position(): string {
      return this.$route?.query.pos || this.positionFromModal;
    },

    assessmentId(): string {
      return this.$route?.query.assessmentID || this.assessmentIdFromModal;
    },

    allowsVideo(): string | boolean {
      return this.currentAssessment?.assessment_request.video_assessment_allowed;
    },

    preferredPlace(): Record<string, any> {
      return this.currentAssessment?.assessment_request.prefered_place || {};
    },

    assessmentLength(): number {
      return store.state.assessmentLength;
    },

    dateTime(): string {
      let dateTime = this.$route?.query.dateTime || this.dateTimeFromModal;

      if (typeof dateTime === 'object') {
        dateTime = dateTime.map((date: string | number | Date) => {
          const startDate = new Date(date);
          const endDate = new Date(
            new Date(date).setHours(startDate.getHours() + this.assessmentLength),
          );

          return {
            date_start: startDate.toJSON(),
            date_end: endDate.toJSON(),
          };
        });
      } else {
        const startDate = new Date(dateTime);
        const endDate = new Date(
          new Date(dateTime).setHours(startDate.getHours() + this.assessmentLength),
        );

        dateTime = [
          {
            date_start: startDate.toJSON(),
            date_end: endDate.toJSON(),
          },
        ];
      }

      return dateTime;
    },

    meetingType(): string {
      return this.assessmentPlace.type;
    },

    userWantsVideoCall(): boolean {
      return this.meetingType === 'an online video-call';
    },
  },

  watch: {
    meetingType(): void {
      if (!this.userWantsVideoCall) this.getLocationData();
    },
  },

  created(): void {
    this.adjustMeetingTypesList();
  },

  methods: {
    async sendInvitation(): Promise<void> {
      formValidators.validateForm(this, 'assessmentPlace');

      if (!this.formIsValid && !this.userWantsVideoCall) {
        return;
      }

      this.processingInvitation = true;

      const payload = {
        dates: this.dateTime,
        show_my_private_phone: this.contactDataCheckboxes[0].showToCandidate,
        show_my_company_phone: this.contactDataCheckboxes[1].showToCandidate,
        show_my_skype: this.contactDataCheckboxes[2].showToCandidate,
        show_my_zoom: this.contactDataCheckboxes[3].showToCandidate,
        show_my_whereby: this.contactDataCheckboxes[4].showToCandidate,
        meeting_type: this.assessmentPlace.type,
        comment: this.assessmentPlace.callInfo,
        location: {
          name: '',
          address: {
            street: this.assessmentPlace.street,
            house_number: this.assessmentPlace.house_number,
            apartment_number: this.assessmentPlace.apartment_number,
            city: this.assessmentPlace.city,
            postal_code: this.assessmentPlace.postal_code,
            country: this.assessmentPlace.country,
          },
        },
      };

      if (this.assessmentPlace.type === 'an online video-call' && !this.assessmentPlace.callInfo) {
        delete payload.comment;
      }

      const resp = await requestAPI({
        url: `/api/v1/assessments/${this.assessmentId}/invite-candidate`,
        method: 'PATCH',
        contentType: 'application/merge-patch+json',
        payload,
      });

      if (resp.ok) {
        this.sentInvite = true;
        this.$emit('changeStageTo3');
        this.$message.success({
          message: this.$t('youHaveSuccessfullySentTheInvitation'),
        });

        if (this.device === 'mobile' || !this.inModal) {
          setTimeout(() => {
            router.push('/assessor-tasks');
          }, 1000);
        }
      } else {
        this.$message.warning({
          message: this.$t('youCannotDoItNow'),
        });
      }

      this.processingInvitation = false;
    },

    adjustMeetingTypesList(): void {
      const allowedVidAssessment = this.allowsVideo;
      if (!allowedVidAssessment) this.typesList.shift();
    },

    getLocationData(): void {
      if (!Object.keys(this.preferredPlace).length) {
        return;
      }

      this.parsingPreferredPlace = true;
      let locationDataSetByClient = 0;

      setTimeout(() => {
        Object.entries(this.preferredPlace).forEach((entry) => {
          if (entry[1] && entry[0] !== 'id') {
            this.assessmentPlace[entry[0]] = entry[1];
            this[`${entry[0]}SetByClient`] = true;
            locationDataSetByClient++;
          }
        });

        if (locationDataSetByClient >= 1 && locationDataSetByClient < 6) {
          this.alertAboutLocationDataSetByClient = this.$t('clientSetSomeLocationData');
        }

        if (locationDataSetByClient === 6) {
          this.alertAboutLocationDataSetByClient = this.$t('clientSetAllLocationData');
        }

        this.parsingPreferredPlace = false;
      }, 1000);
    },

    cleanLocationData(): void {
      this.assessmentPlace.street = '';
      this.assessmentPlace.house_number = '';
      this.assessmentPlace.apartment_number = '';
      this.assessmentPlace.city = '';
      this.assessmentPlace.postal_code = '';
      this.assessmentPlace.country = '';
    },
  },
};
